// components/Admin/ReferrerManagement.jsx
// Referring Physician Management — ported from RIS referrer_view.php
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, CircularProgress, Chip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Save as SaveIcon, Search as SearchIcon, Close as CloseIcon
} from '@mui/icons-material';
import { referrerService } from '../../services/ris';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const emptyForm = {
  referrerId: '', degree: '', firstName: '', lastName: '',
  firstNameEn: '', lastNameEn: '', specialty: '', telephone: '', email: ''
};

export default function ReferrerManagement() {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchReferrers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      const { data } = await referrerService.list(params);
      setReferrers(data.referrers || []);
    } catch (err) {
      console.error('Failed to load referrers:', err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchReferrers(); }, [fetchReferrers]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (ref) => { setEditing(ref); setForm({ ...ref }); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await referrerService.update(editing._id, form);
        setSnackbar({ open: true, message: 'Referrer updated', severity: 'success' });
      } else {
        await referrerService.create(form);
        setSnackbar({ open: true, message: 'Referrer created', severity: 'success' });
      }
      setDialogOpen(false);
      fetchReferrers();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this referrer?')) return;
    try {
      await referrerService.delete(id);
      setSnackbar({ open: true, message: 'Referrer deactivated', severity: 'success' });
      fetchReferrers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to deactivate', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>👨‍⚕️ Referring Physicians</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Referrer</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Search (name, code)"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ endAdornment: <SearchIcon color="action" /> }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip label={`${referrers.length} referrers`} variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Degree</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>English Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Specialty</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : referrers.map(r => (
              <TableRow key={r._id} hover>
                <TableCell>{r.referrerId}</TableCell>
                <TableCell>{r.degree}</TableCell>
                <TableCell>{r.firstName} {r.lastName}</TableCell>
                <TableCell>{r.firstNameEn} {r.lastNameEn}</TableCell>
                <TableCell>{r.specialty}</TableCell>
                <TableCell>
                  <Typography variant="caption">{r.telephone}</Typography>
                  {r.email && <Typography variant="caption" display="block" color="text.secondary">{r.email}</Typography>}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => handleDelete(r._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: '95vw', sm: '640px', md: '680px' },
            maxWidth: '700px !important',
            maxHeight: '88vh',
            borderRadius: '14px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#F1F5F9' : '#0F172A',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            fontSize: '1.05rem',
            fontWeight: 700
          }}
        >
          {editing ? 'Edit Referrer' : 'New Referrer'}
          <IconButton size="small" onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.5, overflowY: 'auto' }}>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Code" value={form.referrerId} size="small"
                onChange={e => setForm(f => ({ ...f, referrerId: e.target.value }))} disabled={!!editing} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Degree (Dr., Prof.)" value={form.degree} size="small"
                onChange={e => setForm(f => ({ ...f, degree: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="First Name" value={form.firstName} size="small"
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Last Name" value={form.lastName} size="small"
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name (EN)" value={form.firstNameEn} size="small"
                onChange={e => setForm(f => ({ ...f, firstNameEn: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name (EN)" value={form.lastNameEn} size="small"
                onChange={e => setForm(f => ({ ...f, lastNameEn: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Specialty" value={form.specialty} size="small"
                onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Telephone" value={form.telephone} size="small"
                onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={form.email} size="small"
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.25,
            borderTop: 1,
            borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1
          }}
        >
          <Button onClick={() => setDialogOpen(false)} sx={{ color: isDark ? '#94A3B8' : '#64748B' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
